import { AxiosResponse } from 'axios';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import MojoApiFactory from '../config/api-factory';
import { StreamEvent } from '../models/chatbot/types';

export interface AISummaryResponse {
  data: {
    query: string;
    summary: string | null;
    hasResults: boolean;
  };
}

// Capability negotiation constants (must match chatbot.ts)
const LINK_FORMAT = 'universal';
const SUPPORTED_CARD_TYPES = 'landscape';

export interface AISummaryRequest {
  query: string;
  model?: string;
}

/**
 * Perform AI summary (non-streaming)
 */
export const performAISummary = async ({ query, model }: AISummaryRequest) => {
  return (await MojoApiFactory.chatbot.post(`search_summary`, {
    query,
    model,
    link_format: LINK_FORMAT,
    card_types: SUPPORTED_CARD_TYPES,
  })) as AxiosResponse<AISummaryResponse>;
};

/**
 * Perform AI summary with streaming (SSE)
 */
export const performAISummaryStream = async ({
  query,
  model,
  onEvent,
}: AISummaryRequest & {
  onEvent: (event: StreamEvent) => void;
}): Promise<void> => {
  fetchEventSource(`${MojoApiFactory.chatbot.defaults.baseURL}search_summary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      // Include authentication headers from the axios instance
      ...(MojoApiFactory.chatbot.defaults.headers.common as Record<
        string,
        string
      >),
    },
    body: JSON.stringify({
      query,
      model,
      link_format: LINK_FORMAT,
      card_types: SUPPORTED_CARD_TYPES,
    }),
    onmessage(event: any) {
      if (event.event === 'ping') return; // Skip empty ping events

      try {
        const data = event.data ? JSON.parse(event.data) : {};
        const eventType = event.event || 'message_update';
        onEvent({
          type: eventType as StreamEvent['type'],
          data,
        } as StreamEvent);
      } catch (e) {
        console.warn('Failed to parse SSE data:', event.data);
      }
    },
    onerror(error: any) {
      onEvent({ type: 'error', data: { error: error.message || error } });
      throw error; // Stop retrying
    },
  });
};
